import { z } from 'zod';

export type ViewAsStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ViewAsSnapshotKey {
  live: string;
  original: string;
  isRestorable: (parked: string) => boolean;
}

const sidebarSnapshotSchema = z.array(z.object({ name: z.string() }).passthrough());

const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

const isSidebarSnapshot = (parked: string): boolean => sidebarSnapshotSchema.safeParse(parseJson(parked)).success;

const anyValue = (): boolean => true;

export const VIEW_AS_SNAPSHOT_KEYS: readonly ViewAsSnapshotKey[] = [
  { live: 'token', original: 'originalToken', isRestorable: anyValue },
  { live: 'sideBar', original: 'originalSideBar', isRestorable: isSidebarSnapshot },
  { live: 'userId', original: 'originalUserId', isRestorable: anyValue },
  { live: 'landingPage', original: 'originalLandingPage', isRestorable: anyValue },
];

const VIEW_AS_SESSION_MARKERS = ['isViewing', 'posthogdetails'] as const;

export const announceViewAsChange = (): void => {
  window.dispatchEvent(new StorageEvent('storage', { key: 'isViewing' }));
};

export interface RestoredViewAsSession {
  landingPage: string | null;
}

export const restoreViewAsSnapshot = (storage: ViewAsStorage): RestoredViewAsSession => {
  const restorable = VIEW_AS_SNAPSHOT_KEYS.flatMap(({ live, original, isRestorable }) => {
    const parked = storage.getItem(original);
    return parked && isRestorable(parked) ? [{ live, parked }] : [];
  });

  restorable.forEach(({ live, parked }) => storage.setItem(live, parked));
  VIEW_AS_SNAPSHOT_KEYS.forEach(({ original }) => storage.removeItem(original));
  VIEW_AS_SESSION_MARKERS.forEach((key) => storage.removeItem(key));

  return { landingPage: restorable.find(({ live }) => live === 'landingPage')?.parked ?? null };
};
